import { createContext, ReactNode, useMemo, useState } from "react";
import { useQuery } from "react-query";

import { getNotifications, Notification } from "@/api";
import { useAppProperties } from "@/hooks";
import { useUser } from "@/providers/context";

interface NotificationWithStatus extends Notification {
  isUnread: boolean;
}

const isValidNotification = (notification: Notification, uid: string) => {
  if (notification.canceled) {
    return false;
  }

  const dateNow = Math.floor(Date.now() / 1000);
  if (notification.send_after > dateNow) {
    return false;
  }

  const senderId = notification.data?.sender_id ?? "";
  if (uid === senderId) {
    return false;
  }

  return true;
};

interface ContextValue {
  isEnabled: boolean;
  notifications: NotificationWithStatus[] | undefined;
  hasUnreadNotifications: boolean;
  markAsRead: () => void;
  isError: boolean;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;
const NotificationsContext = createContext<OptionalContextValue>(undefined);

const NotificationsProvider = ({ children }: ProviderProps) => {
  const { uid } = useUser();
  const { notificationsEnabled } = useAppProperties();

  const localTimestamp = localStorage.getItem("notificationsReadTimestamp");
  const [lastReadTimestamp, setLastReadTimestamp] = useState<
    number | undefined
  >(localTimestamp ? parseInt(localTimestamp) : undefined);

  const query = useQuery(
    ["notifications", uid],
    () => {
      if (uid) {
        return getNotifications({ uid });
      }
    },
    {
      enabled: notificationsEnabled && !!uid,
      refetchInterval: 2 * 60 * 1000,
      refetchIntervalInBackground: false,
    },
  );

  const notifications = useMemo(() => {
    if (!query.data || !uid) {
      return undefined;
    }

    const validNotifications = query.data.filter((n) => {
      return isValidNotification(n, uid);
    });

    const sorted = validNotifications.sort(
      (a, b) => b.send_after - a.send_after,
    );

    return sorted.map((n) => ({
      ...n,
      isUnread: lastReadTimestamp ? n.send_after > lastReadTimestamp : true,
    }));
  }, [query.data, uid, lastReadTimestamp]);

  const hasUnreadNotifications = useMemo(() => {
    if (!notifications) {
      return false;
    }

    return notifications.some(({ isUnread }) => isUnread);
  }, [notifications]);

  const markAsRead = () => {
    const ts = Math.floor(Date.now() / 1000);
    setLastReadTimestamp(ts);
    localStorage.setItem("notificationsReadTimestamp", ts.toString());
  };

  return (
    <NotificationsContext.Provider
      value={{
        isEnabled: notificationsEnabled,
        notifications,
        hasUnreadNotifications,
        markAsRead,
        isError: query.isError,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export { NotificationsContext, NotificationsProvider };
